/**
 * Student Exam Page — שאלון מסכם
 *
 * The sixth tile. Questions are drawn and graded on the server, so this page never
 * touches the AI allowance — a student who has spent it can still be examined.
 */

import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, ArrowRight, Lock } from 'lucide-react'
import ExamRunner from '../components/learning/ExamRunner'
import ExamResultView from '../components/learning/ExamResultView'
import { useLearningStore } from '../store/learningStore'

export default function StudentExamPage() {
  const {
    examAttempt,
    examResult,
    examLoading,
    examError,
    examLockedReason,
    startExam,
    submitExam,
    resetExam,
  } = useLearningStore()
  const headingRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    void startExam('student')
    return () => resetExam()
  }, [startExam, resetExam])

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  const handleRetry = () => {
    resetExam()
    void startExam('student')
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6">
      <nav aria-label="ניווט חזרה">
        <Link to="/learn" className="inline-flex items-center gap-1 text-sm text-primary-700 hover:underline">
          <ArrowRight size={16} aria-hidden="true" />
          חזרה למסלול הלמידה
        </Link>
      </nav>

      <h1 ref={headingRef} tabIndex={-1} className="mt-4 text-2xl font-bold text-gray-900 focus-visible:outline-none">
        שאלון מסכם
      </h1>

      {examLoading && !examAttempt && !examResult && (
        <p className="mt-8 text-center text-sm text-gray-600">מכין את המבחן...</p>
      )}

      {examLockedReason && (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5" role="status">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
            <Lock size={16} aria-hidden="true" />
            {examLockedReason.message}
          </p>
          {examLockedReason.remainingTopics && examLockedReason.remainingTopics.length > 0 && (
            <>
              <p className="mt-3 text-sm text-amber-900">נותרו הנושאים הבאים:</p>
              <ul className="mt-1 list-inside list-disc text-sm text-amber-900">
                {examLockedReason.remainingTopics.map((t) => (
                  <li key={t.slug}>
                    <Link to={`/learn/topic/${t.slug}`} className="underline">
                      {t.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
          <Link to="/learn" className="btn btn-secondary mt-4 inline-flex">
            חזרה למסלול הלמידה
          </Link>
        </div>
      )}

      {examError && (
        <div className="mt-6 flex items-center gap-2 rounded-xl bg-red-50 p-4 text-sm text-red-800" role="alert">
          <AlertCircle size={18} aria-hidden="true" />
          {examError}
        </div>
      )}

      {examResult && <div className="mt-6"><ExamResultView result={examResult} onRetry={handleRetry} /></div>}

      {examAttempt && !examResult && (
        <div className="mt-6">
          <ExamRunner
            questions={examAttempt.questions}
            passScorePercent={examAttempt.passScorePercent}
            submitting={examLoading}
            onSubmit={(answers) => void submitExam('student', answers)}
          />
        </div>
      )}
    </div>
  )
}
